import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { NAME_PATTERN, PNR_PATTERN, unsubscribe } from '@fcom/core/utils';
import { ButtonTheme, ButtonType, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { RefundsService } from '@fcom/refunds-common/services/refunds.service';

@Component({
  selector: 'fin-refund-status-checker-form',
  templateUrl: 'refund-status-checker-form.component.html',
  styleUrls: ['./refund-status-checker-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RefundStatusCheckerFormComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  form: UntypedFormGroup;
  loading = false;
  checkFailed = false;

  private subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private languageService: LanguageService,
    private store$: Store<AppState>,
    private refundsService: RefundsService,
    @Inject(PLATFORM_ID) private platform: object,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.createForm();

    if (isPlatformBrowser(this.platform)) {
      this.subscription.add(
        this.store$
          .pipe(
            profileOrUndefinedIfNotLoggedIn(),
            take(1),
            filter((profile) => !!profile?.lastname && !this.form.touched)
          )
          .subscribe((profile) => {
            this.form.get('lastName').setValue(profile?.lastname ?? '');
            this.cdr.markForCheck();
          })
      );
    }
    this.subscription.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        const recLoc = params['recLoc'];
        const lastName = params['lastName'];
        if (recLoc && lastName) {
          this.handleFormSubmission(recLoc, lastName);
        }
        this.router.navigate([], { queryParams: {} });
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAsDirty();
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
    } else if (!this.form.disabled) {
      this.handleFormSubmission(this.form.get('recLoc').value, this.form.get('lastName').value);
    }
  }

  private handleFormSubmission(recLoc: string, lastName: string): void {
    this.form.disable();
    this.loading = true;
    this.checkFailed = false;

    this.subscription.add(
      this.refundsService
        .fetchRefunds(recLoc, lastName)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.router.navigate([this.languageService.langValue, 'refunds']);
          },
          error: () => {
            this.checkFailed = true;
            this.loading = false;
            this.form.enable();
            this.cdr.markForCheck();
          },
        })
    );
  }

  private createForm(): void {
    this.form = this.fb.group({
      lastName: [
        '',
        [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(NAME_PATTERN)],
      ],
      recLoc: ['', [Validators.required, Validators.pattern(PNR_PATTERN)]],
    });
  }
}
